// ProgramPanel — a single assigned program as its own self-contained "entry"
// (#74): a journey, not a bag of cards. A brand progress ring + identity header,
// then the program's modules as an ordered CHECKLIST of CourseRows (the shared
// row used across the whole page). One primary "Fortsätt" action jumps to the
// next unfinished module. Built entirely on Nexilink tokens + palette.
import React from 'react';
import { Check, Play } from 'lucide-react';
import type { LearningProgramView, LearningModuleView } from '@/lib/models/learning';
import { StatusBadge } from '@/components/custom-ui/status-badge';
import { CourseRow } from './CourseRow';
import { NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';

const BRAND = '#1A4D4F';   // deep teal
const SAGE = '#A4C3B2';    // sage — the ring track

// Brand progress ring: sage track, teal fill, teal % / check. Belongs to the
// Nexilink palette rather than a generic gauge.
const ProgressRing: React.FC<{ value: number; size?: number }> = ({ value, size = 52 }) => {
    const stroke = 5;
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const v = Math.max(0, Math.min(100, value));
    const offset = circ - (v / 100) * circ;
    const done = v >= 100;
    return (
        <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="-rotate-90">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={SAGE} strokeOpacity={0.35} strokeWidth={stroke} />
                <circle
                    cx={size / 2} cy={size / 2} r={r} fill="none" strokeLinecap="round"
                    stroke={BRAND} strokeWidth={stroke}
                    strokeDasharray={circ} strokeDashoffset={offset} className="transition-all duration-500"
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
                {done ? <Check className="w-5 h-5" style={{ color: BRAND }} /> : <span className="text-xs font-bold" style={{ color: BRAND }}>{v}%</span>}
            </div>
        </div>
    );
};

export const ProgramPanel: React.FC<{
    program: LearningProgramView;
    onOpenModule: (moduleId: string) => void;
}> = ({ program, onOpenModule }) => {
    const nextIdx = (() => {
        const ip = program.modules.findIndex((m) => m.status === 'in_progress');
        if (ip >= 0) return ip;
        return program.modules.findIndex((m) => m.status === 'not_started' && !m.locked);
    })();
    const nextModule: LearningModuleView | undefined = nextIdx >= 0 ? program.modules[nextIdx] : undefined;
    const allDone = program.total > 0 && program.completed === program.total;
    const ctaLabel = program.completed > 0 ? 'Fortsätt' : 'Börja';

    return (
        <div className={NEXILINK_CARD_WHITE_ELEVATED}>
            {/* Header */}
            <div className="flex items-center gap-4 p-5 border-b border-gray-100">
                <ProgressRing value={program.percentage} />
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <h2 className="text-base font-bold text-gray-900 truncate">{program.name}</h2>
                        {allDone && <StatusBadge status="completed" />}
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5 truncate">
                        {program.organisation_name ? `${program.organisation_name} · ` : ''}
                        {program.completed} av {program.total} moduler klara
                    </p>
                </div>
                {nextModule && (
                    <button
                        type="button"
                        onClick={() => onOpenModule(nextModule.id)}
                        className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-white px-4 py-2 rounded-xl flex-shrink-0 transition-opacity hover:opacity-90"
                        style={{ backgroundColor: BRAND }}
                    >
                        <Play className="w-4 h-4 fill-white" /> {ctaLabel}
                    </button>
                )}
            </div>

            {/* Module checklist — the shared CourseRow */}
            <div className="p-2 divide-y divide-gray-50">
                {program.modules.map((m, i) => (
                    <CourseRow
                        key={m.id}
                        title={m.name}
                        organisationName={m.organisation_name}
                        organisationLogoUrl={m.organisation_logo_url}
                        estimatedDuration={m.estimated_duration}
                        totalSteps={m.total_steps}
                        status={m.status}
                        locked={m.locked}
                        percentage={m.percentage}
                        isNext={i === nextIdx}
                        actionLabel={m.status === 'in_progress' ? 'Fortsätt' : 'Börja'}
                        onClick={() => onOpenModule(m.id)}
                    />
                ))}
            </div>
        </div>
    );
};

export default ProgramPanel;
